import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from 'recharts'
import { AlertTriangle, Users, Activity, CheckCircle2 } from 'lucide-react'
import { cn } from '../lib/utils'
import {
  alerts,
  flaggedCustomers,
  ruleHits,
  caseFunnel,
  kpiCards,
  severityPalette,
} from '../data/mockData'

function KpiCard({ label, value, delta, tone, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <div className="rounded-lg bg-slate-100 p-2 text-slate-600">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p
        className={cn(
          'mt-3 text-sm font-medium',
          tone === 'positive' ? 'text-emerald-600' : 'text-rose-600'
        )}
      >
        {delta} <span className="text-slate-400 font-normal">vs. last 24h</span>
      </p>
    </div>
  )
}

function SeverityBadge({ severity }) {
  const { label, color } = severityPalette[severity] || severityPalette.low
  return <span className={cn('badge ring-1', color)}>{label}</span>
}

export function TmsDashboard() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Risk Monitoring Dashboard</h1>
          <p className="text-sm text-slate-500">Real-time alert feed, flagged customers, and case funnel.</p>
        </div>
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
          Live demo mode
        </span>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={AlertTriangle} {...kpiCards[0]} />
        <KpiCard icon={Users} {...kpiCards[1]} />
        <KpiCard icon={Activity} {...kpiCards[2]} />
        <KpiCard icon={CheckCircle2} {...kpiCards[3]} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Alert Feed</h2>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Alert</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {alerts.map((alert) => (
                  <tr key={alert.id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900">{alert.id}</td>
                    <td className="px-4 py-3"><SeverityBadge severity={alert.severity} /></td>
                    <td className="px-4 py-3 text-sm text-slate-700">{alert.message}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">{alert.customer}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">{alert.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Case Funnel</h2>
          <div className="space-y-4">
            {caseFunnel.map((stage) => (
              <div key={stage.name}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{stage.name}</span>
                  <span className="font-semibold text-slate-900">{stage.value}</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-slate-100">
                  <div
                    className="h-2.5 rounded-full"
                    style={{
                      width: `${(stage.value / caseFunnel[0].value) * 100}%`,
                      backgroundColor: stage.fill,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Rule Hits (Last 24h)</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ruleHits} layout="vertical" margin={{ left: 24, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {ruleHits.map((_, i) => (
                    <Cell key={i} fill={['#0ea5e9', '#f59e0b', '#f97316', '#8b5cf6', '#ec4899', '#22c55e'][i % 6]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Top Flagged Customers</h2>
            <span className="text-xs text-slate-500">Synthetic data</span>
          </div>
          <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Hits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {flaggedCustomers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.id}</div>
                      <div className="text-xs text-slate-400">{c.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className={cn(
                          'inline-flex rounded-md px-2 py-1 text-xs font-semibold',
                          c.riskScore >= 90 ? 'bg-risk-critical/10 text-risk-critical' :
                          c.riskScore >= 75 ? 'bg-risk-high/10 text-risk-high' :
                          'bg-risk-medium/10 text-risk-medium'
                        )}
                      >
                        {c.riskScore}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{c.hits}</td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'badge capitalize',
                        c.status === 'active' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'
                      )}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  )
}
